import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Base locale imports
import enCommon from "./locales/en/common.json";
import esCommon from "./locales/es/common.json";

// Tenant-specific overrides (English)
import byuEn from "./tenants/byu.json";
import wvuEn from "./tenants/wvu.json";
import fsuEn from "./tenants/fsu.json";
import unisunEn from "./tenants/unisun.json";

// ============================================================================
// Types
// ============================================================================

export type Messages = typeof enCommon;
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

// Supported languages
export const SUPPORTED_LANGUAGES = ["en", "es"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: "English",
  es: "Español",
};

// ============================================================================
// Utilities
// ============================================================================

/**
 * Deep merge utility for nested objects
 */
const deepMerge = <T extends Record<string, unknown>>(
  base: T,
  override: Partial<T>
): T => {
  const result = { ...base } as T;

  for (const key in override) {
    if (Object.prototype.hasOwnProperty.call(override, key)) {
      const baseValue = base[key];
      const overrideValue = override[key];

      if (
        typeof baseValue === "object" &&
        baseValue !== null &&
        typeof overrideValue === "object" &&
        overrideValue !== null &&
        !Array.isArray(baseValue)
      ) {
        result[key] = deepMerge(
          baseValue as Record<string, unknown>,
          overrideValue as Record<string, unknown>
        ) as T[Extract<keyof T, string>];
      } else if (overrideValue !== undefined) {
        result[key] = overrideValue as T[Extract<keyof T, string>];
      }
    }
  }

  return result;
};

// ============================================================================
// Tenant Translation Registry
// ============================================================================

// Tenant overrides per language
const tenantTranslations: Record<string, Record<SupportedLanguage, DeepPartial<Messages>>> = {
  byu: {
    en: byuEn as DeepPartial<Messages>,
    es: {}, // Add Spanish overrides for BYU when available
  },
  wvu: {
    en: wvuEn as DeepPartial<Messages>,
    es: {}, // Add Spanish overrides for WVU when available
  },
  fsu: {
    en: fsuEn as DeepPartial<Messages>,
    es: {}, // Add Spanish overrides for FSU when available
  },
  unisun: {
    en: unisunEn as DeepPartial<Messages>,
    es: {}, // Add Spanish overrides for Unisun when available
  },
};

/**
 * Get tenant-specific translations for a language
 */
export const getTenantTranslations = (
  tenantId: string,
  language: SupportedLanguage = "en"
): DeepPartial<Messages> => {
  return tenantTranslations[tenantId]?.[language] ?? {};
};

// ============================================================================
// Legacy API (backwards compatible)
// ============================================================================

export const baseMessages = enCommon;

/**
 * Get merged messages for a specific tenant (legacy API)
 * @deprecated Use useTranslation hook with tenant context instead
 */
export const getMessages = (tenantId: string, language: SupportedLanguage = "en"): Messages => {
  const base = language === "es" ? esCommon : enCommon;
  const overrides = getTenantTranslations(tenantId, language);
  return deepMerge(base, overrides as Partial<Messages>);
};

/**
 * Get a nested value from messages using dot notation
 * @deprecated Use useTranslation hook instead
 */
export const getMessage = (
  messages: Messages,
  path: string,
  fallback?: string
): string => {
  const keys = path.split(".");
  let current: unknown = messages;

  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return fallback ?? path;
    }
  }

  return typeof current === "string" ? current : fallback ?? path;
};

// ============================================================================
// i18next Initialization
// ============================================================================

let currentTenantId: string | null = null;

/**
 * Initialize i18n with tenant-specific overrides
 * Call this early in app initialization with the resolved tenant
 */
export const initI18n = (
  tenantId?: string,
  configTranslations?: Record<string, Record<string, unknown>>
) => {
  currentTenantId = tenantId ?? null;

  // Build resources: base bundle → static tenant overrides → config overrides.
  const resources: Record<string, { common: Messages }> = {};

  for (const lang of SUPPORTED_LANGUAGES) {
    const base = lang === "es" ? esCommon : enCommon;
    const tenantOverrides = tenantId ? getTenantTranslations(tenantId, lang) : {};
    const configOverrides = (configTranslations?.[lang] ?? {}) as DeepPartial<Messages>;
    const merged = deepMerge(
      deepMerge(base, tenantOverrides as Partial<Messages>),
      configOverrides as Partial<Messages>
    );
    resources[lang] = { common: merged };
  }

  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: "en",
      defaultNS: "common",
      ns: ["common"],
      detection: {
        order: ["localStorage", "navigator", "htmlTag"],
        caches: ["localStorage"],
        lookupLocalStorage: "i18nextLng",
      },
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false, // Disable suspense for easier SSR/CSR handling
      },
    });

  return i18n;
};

/**
 * Update tenant translations at runtime (e.g., after tenant context loads)
 */
export const updateTenantTranslations = (tenantId: string) => {
  if (currentTenantId === tenantId) return; // Already set
  currentTenantId = tenantId;

  for (const lang of SUPPORTED_LANGUAGES) {
    const base = lang === "es" ? esCommon : enCommon;
    const tenantOverrides = getTenantTranslations(tenantId, lang);
    const merged = deepMerge(base, tenantOverrides as Partial<Messages>);

    // Update the resource bundle
    i18n.addResourceBundle(lang, "common", merged, true, true);
  }
};

/**
 * Get current tenant ID
 */
export const getCurrentTenantId = () => currentTenantId;

// ============================================================================
// Re-exports
// ============================================================================

export { i18n };
export { useTranslation, Trans, I18nextProvider } from "react-i18next";
export type { TFunction } from "i18next";
